import {
  type ColumnType,
  type Selectable,
  type Insertable,
  type Updateable,
} from 'kysely'
import { z } from 'zod'

import {
  webhookEventStatus,
  type default as WebhookEventStatus,
} from './WebhookEventStatus.js'

/** Identifier type for public.webhook_event_ */
export type WebhookEventId = string & { __brand: 'WebhookEventId' }

/** Represents the table public.webhook_event_ */
export default interface WebhookEventTable {
  /** Database type: pg_catalog.uuid */
  id: ColumnType<WebhookEventId, WebhookEventId | undefined, WebhookEventId>

  /** Database type: pg_catalog.timestamptz */
  createdAt: ColumnType<Date | null, Date | string | null, Date | string | null>

  /** Database type: pg_catalog.uuid */
  createdBy: ColumnType<string | null, string | null, string | null>

  /** Database type: pg_catalog.timestamptz */
  updatedAt: ColumnType<Date | null, Date | string | null, Date | string | null>

  /** Database type: pg_catalog.uuid */
  updatedBy: ColumnType<string | null, string | null, string | null>

  /** Database type: pg_catalog.timestamptz */
  deletedAt: ColumnType<Date | null, Date | string | null, Date | string | null>

  /** Database type: pg_catalog.uuid */
  deletedBy: ColumnType<string | null, string | null, string | null>

  /** Database type: pg_catalog.text */
  doctype: ColumnType<string, string, string>

  /** Database type: pg_catalog.jsonb */
  payload: ColumnType<unknown, unknown, unknown>

  /** Database type: pg_catalog.text */
  erpnextDocName: ColumnType<string, string, string>

  /** Database type: public.webhook_event_status_ */
  status: ColumnType<
    WebhookEventStatus,
    WebhookEventStatus | undefined,
    WebhookEventStatus
  >

  /** Database type: pg_catalog.text */
  errorMessage: ColumnType<string | null, string | null, string | null>

  /** Database type: pg_catalog.int4 */
  attempts: ColumnType<number, number | undefined, number>

  /** Database type: pg_catalog.timestamptz */
  processedAt: ColumnType<
    Date | null,
    Date | string | null,
    Date | string | null
  >
}

export type WebhookEvent = Selectable<WebhookEventTable>

export type WebhookEventInitializer = Insertable<WebhookEventTable>

export type WebhookEventMutator = Updateable<WebhookEventTable>

export const webhookEventId = z.string() as unknown as z.Schema<WebhookEventId>

export const webhookEvent = z.object({
  id: webhookEventId,
  createdAt: z.coerce.date().nullable().optional(),
  createdBy: z.string().nullable().optional(),
  updatedAt: z.coerce.date().nullable().optional(),
  updatedBy: z.string().nullable().optional(),
  deletedAt: z.coerce.date().nullable().optional(),
  deletedBy: z.string().nullable().optional(),
  doctype: z.string(),
  payload: z.unknown(),
  erpnextDocName: z.string(),
  status: webhookEventStatus,
  errorMessage: z.string().nullable().optional(),
  attempts: z.number(),
  processedAt: z.coerce.date().nullable().optional(),
}) as unknown as z.Schema<WebhookEvent>

export const webhookEventInitializer = z.object({
  id: webhookEventId.optional(),
  createdAt: z.coerce.date().optional().nullable(),
  createdBy: z.string().optional().nullable(),
  updatedAt: z.coerce.date().optional().nullable(),
  updatedBy: z.string().optional().nullable(),
  deletedAt: z.coerce.date().optional().nullable(),
  deletedBy: z.string().optional().nullable(),
  doctype: z.string(),
  payload: z.unknown(),
  erpnextDocName: z.string(),
  status: webhookEventStatus.optional(),
  errorMessage: z.string().optional().nullable(),
  attempts: z.number().optional(),
  processedAt: z.coerce.date().optional().nullable(),
}) as unknown as z.Schema<WebhookEventInitializer>

export const webhookEventMutator = z.object({
  id: webhookEventId.optional(),
  createdAt: z.coerce.date().optional().nullable(),
  createdBy: z.string().optional().nullable(),
  updatedAt: z.coerce.date().optional().nullable(),
  updatedBy: z.string().optional().nullable(),
  deletedAt: z.coerce.date().optional().nullable(),
  deletedBy: z.string().optional().nullable(),
  doctype: z.string().optional(),
  payload: z.unknown().optional(),
  erpnextDocName: z.string().optional(),
  status: webhookEventStatus.optional(),
  errorMessage: z.string().optional().nullable(),
  attempts: z.number().optional(),
  processedAt: z.coerce.date().optional().nullable(),
}) as unknown as z.Schema<WebhookEventMutator>
